import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { Box, CircularProgress, Typography } from '@mui/material';
import { getRuntimeConfig } from '../runtimeConfig';
import { auth } from '../firebase';

function getDefaultWebSocketBaseUrl() {
    if (typeof window === 'undefined') {
        return '';
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
}

const WS_BASE_URL = getRuntimeConfig('TERMINAL_WS_BASE_URL', getDefaultWebSocketBaseUrl());
const Terminal = forwardRef(({ sessionSlug, courseId, testId, height = '80vh', readOnly = false }, ref) => {
    const terminalRef = useRef(null);
    const xtermRef = useRef(null);
    const socketRef = useRef(null);
    const commandQueueRef = useRef([]);
    const [loading, setLoading] = useState(true);

    useImperativeHandle(ref, () => ({
        executeCommand(command) {
            const normalizedCommand = String(command || '').trim();
            if (!normalizedCommand) return;

            const payload = new TextEncoder().encode(`${normalizedCommand}\r`);
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(payload);
            } else {
                commandQueueRef.current.push(payload);
            }
        },
    }), []);

    useEffect(() => {
        // Show spinner for 10 seconds
        const loadingTimer = setTimeout(() => {
            setLoading(false);
        }, 10000);

        // Initialize terminal
        const terminal = new XTerm({
            cursorBlink: true,
            fontSize: 14,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            theme: {
                background: '#0f172a',
                foreground: '#e2e8f0',
                cursor: '#818cf8',
                selectionBackground: 'rgba(129, 140, 248, 0.35)',
            },
        });

        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.open(terminalRef.current);
        fitAddon.fit();
        xtermRef.current = terminal;

        let socket;
        let cancelled = false;

        const sendResize = () => {
            if (!socket || socket.readyState !== WebSocket.OPEN) {
                return;
            }
            fitAddon.fit();
            socket.send(JSON.stringify({
                type: 'resize',
                cols: terminal.cols,
                rows: terminal.rows,
            }));
        };

        const resizeObserver = new ResizeObserver(sendResize);
        resizeObserver.observe(terminalRef.current);

        const encoder = new TextEncoder();
        const connect = async () => {
            const idToken = await auth.currentUser?.getIdToken();
            if (!idToken) {
                throw new Error('You must be logged in to open a terminal.');
            }
            const ticketResponse = await fetch('/terminal-ticket', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ courseId, sessionId: sessionSlug, ...(testId ? { testId } : {}) }),
            });
            const ticketPayload = await ticketResponse.json().catch(() => null);
            if (!ticketResponse.ok || !ticketPayload?.ticket) {
                throw new Error(ticketPayload?.error || 'Terminal authorization failed.');
            }
            if (cancelled) return;

            const socketUrl = `${WS_BASE_URL}/ws/${encodeURIComponent(courseId)}/${encodeURIComponent(sessionSlug)}?ticket=${encodeURIComponent(ticketPayload.ticket)}`;
            socket = new WebSocket(socketUrl);
            socketRef.current = socket;
            socket.binaryType = 'arraybuffer';

            socket.onopen = () => {
                if (socketRef.current !== socket) {
                    socket.close();
                    return;
                }
                terminal.writeln('Connected to the backend terminal!');
                sendResize();
                while (commandQueueRef.current.length > 0 && socket.readyState === WebSocket.OPEN) {
                    socket.send(commandQueueRef.current[0]);
                    // Retain each command until the active socket has accepted it.
                    // In particular, Strict Mode's simulated cleanup must not lose
                    // a command queued during the first effect setup.
                    commandQueueRef.current.shift();
                }
            };
            socket.onmessage = (event) => {
                if (typeof event.data === 'string') {
                    terminal.write(event.data);
                    return;
                }
                terminal.write(new Uint8Array(event.data));
            };
            socket.onerror = () => terminal.writeln('WebSocket connection error.');
            socket.onclose = (event) => terminal.writeln(`Connection closed (${event.code || 'no code'}).`);
        };

        connect().catch((error) => {
            if (!cancelled) {
                terminal.writeln(`Terminal unavailable: ${error.message}`);
                setLoading(false);
            }
        });

        const dataDisposable = terminal.onData((data) => {
            if (readOnly) return;
            if (socket?.readyState === WebSocket.OPEN) {
                socket.send(encoder.encode(data));
            }
        });

        return () => {
            cancelled = true;
            clearTimeout(loadingTimer);
            resizeObserver.disconnect();
            dataDisposable.dispose();
            terminal.dispose();
            socket?.close();
            if (socketRef.current === socket) {
                socketRef.current = null;
            }
        };
    }, [sessionSlug, courseId, testId, readOnly]);

    return (
        <Box sx={{
            position: 'relative',
            height,
            width: '100%',
            border: '1px solid',
            borderColor: '#1e293b',
            borderRadius: 1,
            boxShadow: '0 18px 32px rgba(15, 23, 42, 0.24)',
            overflow: 'hidden'
        }}>
            {loading && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(15, 23, 42, 0.8)',
                    zIndex: 2
                }}>
                    <Typography
                        variant="h6"
                        sx={{ color: 'white', mb: 2 }}
                    >
                        Preparing environment
                    </Typography>
                    <CircularProgress
                        size={60}
                        thickness={4}
                        sx={{ color: 'primary.main' }}
                    />
                </Box>
            )}

            <Box
                ref={terminalRef}
                sx={{
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'common.black',
                    p: 1,
                    background: 'linear-gradient(180deg, #111827 0%, #0f172a 100%)',
                }}
            />
        </Box>
    );
});

Terminal.displayName = 'Terminal';

export default Terminal;
